import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  changeOrderStatus,
  changeOrderCustomer,
  getPublishedOrderBooks,
  getOrderById,
  getOrderPaymentPlan,
  addOrderPaymentPlan,
  getOrderPayments,
  addOrderPayment,
  deleteOrderPayment,
  getOrderReturns,
} from '../services/order_service'
import { getCustomerTypes, getAllCustomers } from '../services/customer_service'
import { openPrintableDocument } from '../common/printableDocument'
import './OderDetail.css'

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback

const getOrderId = (order) =>
  getValue(order, ['orderid', 'orderId', 'order_id', 'id'])
const getOptionValue = (option) =>
  typeof option === 'object'
    ? getValue(option, ['id', 'value', 'customerid', 'customer_id', 'userid'])
    : option
const getOptionLabel = (option) =>
  typeof option === 'object'
    ? getValue(option, [
        'name',
        'label',
        'cust_name',
        'customer_name',
        'customertype',
        'type',
      ])
    : option
const getCustomerId = (order) =>
  getValue(order, ['customerid', 'customer_id', 'customerId', 'cust_id'], '')
const getCustomerName = (order) =>
  getValue(order, ['customer_name', 'customerName', 'cust_name', 'customer'])
const getOrderStatus = (order) =>
  getValue(order, ['status', 'order_status'], 'Published')
const getBookTitle = (book) =>
  getValue(book, ['book_title', 'bookTitle', 'description', 'title', 'name'])
const getBookId = (book) =>
  getValue(book, ['bookid', 'book_id', 'bookId', 'id'])
const getQuantity = (book) =>
  Number(getValue(book, ['quantity', 'ordered_quantity', 'qty'], 0))
const getUnitCost = (book) =>
  Number(
    getValue(
      book,
      ['unitprice', 'unitPrice', 'unitcost', 'unitCost', 'price'],
      0,
    ),
  )
const getDiscount = (book) => {
  const value = Number(getValue(book, ['discount', 'discount_percent'], 0))
  return value > 0 && value < 1 ? value * 100 : value
}
const getReturnedQuantity = (book) =>
  Number(
    getValue(book, ['totalreturned', 'totalReturned', 'returned_quantity'], 0),
  )
const getPaymentPlanDate = (item) =>
  getValue(item, ['plan_date', 'duedate', 'dueDate', 'date', 'payment_date'])
const getPaymentPlanAmount = (item) =>
  Number(getValue(item, ['plan_amount', 'payment_amount', 'amountDue'], 0))
const getPaymentPlanStatus = (item) =>
  getValue(item, ['payplan_status', 'payment_status'], 'Pending')
const getPaymentId = (payment) =>
  getValue(payment, ['order_paymentid', 'payment_id', 'id'])
const getPaymentDate = (payment) =>
  getValue(payment, ['payment_date', 'pay_date', 'date'])
const getPaymentMode = (payment) =>
  getValue(payment, ['pay_mode', 'payment_mode', 'mode'])
const getPaymentBankName = (payment) =>
  getValue(payment, ['bank_name', 'bankName'], '')
const getPaymentChequeNumber = (payment) =>
  getValue(payment, ['cheque_number', 'chequeNumber', 'cheque_no'], '')
const getPaymentReceipt = (payment) =>
  getValue(payment, ['receipt_number', 'receipt', 'receiptNumber'])
const getPaymentAmount = (payment) =>
  Number(getValue(payment, ['total_amount', 'payment_amount'], 0))
const getPaymentEnteredBy = (payment) =>
  getValue(payment, [
    'received_by_user',
    'entered_by',
    'user_fullname',
    'fullname',
  ])
const getPaymentRemarks = (payment) =>
  getValue(payment, ['payment_remarks', 'comment', 'note'], '')
const getPaymentBookId = (payment) =>
  getValue(payment, ['bookid', 'book_id'], '')
const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}

function OderDetail() {
  const { orderId } = useParams()
  const { user, hasPermission } = useAuth()
  const canApproveOrder = hasPermission('approveOrder')
  const canUnapproveOrder = hasPermission('unapproveOrder')
  const canReleaseOrder = hasPermission('releaseOrder')
  const canAddPaymentPlan = hasPermission('addPaymentPlan')
  const canAddPayment = hasPermission('addPayment')
  const canDeletePayment = hasPermission('deletePayment')
  const location = useLocation()
  const navigate = useNavigate()
  const [order, setOrder] = useState(null)
  const [books, setBooks] = useState([])
  const [activeTab, setActiveTab] = useState('payment-plan')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [statusModal, setStatusModal] = useState(null)
  const [statusDate, setStatusDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  )
  const [statusComment, setStatusComment] = useState('')
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false)
  const [customerTypes, setCustomerTypes] = useState([])
  const [customers, setCustomers] = useState([])
  const [customerTypeOption, setCustomerTypeOption] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [isUpdatingCustomer, setIsUpdatingCustomer] = useState(false)
  const [orderPaymentPlan, setOrderPaymentPlan] = useState(null)
  const [orderPayments, setOrderPayments] = useState([])
  const [orderReturns, setOrderReturns] = useState([])
  const [isPaymentPlanModalOpen, setIsPaymentPlanModalOpen] = useState(false)
  const [paymentPlanDate, setPaymentPlanDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  )
  const [paymentPlanAmount, setPaymentPlanAmount] = useState('')
  const [isAddingPaymentPlan, setIsAddingPaymentPlan] = useState(false)
  const [isPaymentTypeMenuOpen, setIsPaymentTypeMenuOpen] = useState(false)
  const [paymentModal, setPaymentModal] = useState(null)
  const [paymentDate, setPaymentDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  )
  const [paymentMode, setPaymentMode] = useState('Cash')
  const [paymentBankName, setPaymentBankName] = useState('')
  const [paymentChequeNumber, setPaymentChequeNumber] = useState('')
  const [paymentReceipt, setPaymentReceipt] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentRemarks, setPaymentRemarks] = useState('')
  const [selectedPaymentBookIds, setSelectedPaymentBookIds] = useState([])
  const [isSavingPayment, setIsSavingPayment] = useState(false)
  const [deletingPaymentId, setDeletingPaymentId] = useState(null)
  const [paymentToDelete, setPaymentToDelete] = useState(null)
  const [viewingPayment, setViewingPayment] = useState(null)
  useEffect(() => {
    let isMounted = true
    const loadOrder = async () => {
      setIsLoading(true)
      setError('')
      try {
        const orderResponse = await getOrderById(orderId)
        if (!orderResponse) {
          throw new Error(`Order ${orderId} could not be found.`)
        }
        if (!isMounted) return
        setOrder(orderResponse)

        const embeddedBooks = getValue(
          orderResponse,
          ['orderBooks', 'order_books', 'books', 'items'],
          null,
        )
        if (Array.isArray(embeddedBooks)) {
          setBooks(embeddedBooks)
        } else if (getCustomerId(orderResponse)) {
          setBooks(await getPublishedOrderBooks(getCustomerId(orderResponse)))
        }
      } catch (requestError) {
        if (isMounted) setError(requestError.message)
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }
    const loadOrderPaymentPlan = async () => {
      try {
        const paymentPlan = await getOrderPaymentPlan(orderId)
        setOrderPaymentPlan(paymentPlan)
      } catch (requestError) {
        console.error(requestError)
      }
    }
    const loadOrderPayments = async () => {
      try {
        const payments = await getOrderPayments(orderId)
        setOrderPayments(payments)
      } catch (requestError) {
        console.error(requestError)
      }
    }
    const loadOrderReturns = async () => {
      try {
        const returns = await getOrderReturns(orderId)
        setOrderReturns(returns)
      } catch (requestError) {
        console.error(requestError)
      }
    }
    loadOrderPaymentPlan()
    loadOrderPayments()
    loadOrderReturns()
    loadOrder()
    return () => {
      isMounted = false
    }
  }, [location.state, orderId])

  const submitStatusChange = async (event) => {
    event.preventDefault()
    if (!statusModal) return

    const isApproval = statusModal === 'approve'
    setIsUpdatingStatus(true)
    setError('')
    try {
      await changeOrderStatus(
        getOrderId(order),
        isApproval ? 'approved' : 'requested',
        isApproval
          ? {
              approved_date: statusDate,
              approval_comment: statusComment.trim(),
              user: user?.userid ?? user?.user_id ?? user?.id,
            }
          : {
              unapproved_date: '',
              unapproval_comment: '',
              user: user?.userid ?? user?.user_id ?? user?.id,
            },
      )
      setOrder(await getOrderById(orderId))
      setStatusModal(null)
      setStatusComment('')
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const openCustomerModal = async () => {
    setIsCustomerModalOpen(true)
    setSelectedCustomerId('')
    try {
      const loadedCustomerTypes = await getCustomerTypes()
      setCustomerTypes(loadedCustomerTypes)
      const defaultType = getOptionValue(loadedCustomerTypes[0]) ?? ''
      setCustomerTypeOption(defaultType)
      setCustomers(await getAllCustomers(defaultType))
    } catch (requestError) {
      setError(requestError.message)
    }
  }

  const handleCustomerTypeOptionChange = async (event) => {
    const type = event.target.value
    setCustomerTypeOption(type)
    setSelectedCustomerId('')
    try {
      setCustomers(await getAllCustomers(type))
    } catch (requestError) {
      setCustomers([])
      setError(requestError.message)
    }
  }

  const submitCustomerChange = async (event) => {
    event.preventDefault()
    if (!selectedCustomerId) return

    setIsUpdatingCustomer(true)
    setError('')
    try {
      await changeOrderCustomer(getOrderId(order), selectedCustomerId)
      setOrder(await getOrderById(orderId))
      setIsCustomerModalOpen(false)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsUpdatingCustomer(false)
    }
  }

  const openPaymentPlanModal = () => {
    setPaymentPlanDate(new Date().toISOString().slice(0, 10))
    setPaymentPlanAmount('')
    setIsPaymentPlanModalOpen(true)
  }

  const submitPaymentPlan = async (event) => {
    event.preventDefault()
    if (!paymentPlanDate || !paymentPlanAmount) return

    setIsAddingPaymentPlan(true)
    setError('')
    try {
      await addOrderPaymentPlan(orderId, {
        due_date: paymentPlanDate,
        amount: paymentPlanAmount,
      })
      setOrderPaymentPlan(await getOrderPaymentPlan(orderId))
      setIsPaymentPlanModalOpen(false)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsAddingPaymentPlan(false)
    }
  }

  const openPaymentModal = (type) => {
    setIsPaymentTypeMenuOpen(false)
    setPaymentModal(type)
    setPaymentDate(new Date().toISOString().slice(0, 10))
    setPaymentMode('Cash')
    setPaymentBankName('')
    setPaymentChequeNumber('')
    setPaymentReceipt('')
    setPaymentAmount('')
    setPaymentRemarks('')
    setSelectedPaymentBookIds([])
  }

  const closePaymentModal = () => {
    setPaymentModal(null)
  }

  const submitPayment = async (event) => {
    event.preventDefault()
    if (!paymentDate || !paymentAmount) return

    const paymentValue = Number(paymentAmount)
    if (paymentValue > orderBalance) {
      setError('Payment amount cannot exceed the total order balance.')
      return
    }
    if (
      paymentModal === 'specific_books' &&
      selectedPaymentBookIds.length === 0
    ) {
      setError('Select at least one book for this payment.')
      return
    }

    setIsSavingPayment(true)
    setError('')
    try {
      const isWithholdingTax = paymentModal === 'withholding_tax'
      const payload = {
        payment_date: paymentDate,
        pay_mode: isWithholdingTax ? '' : paymentMode,
        bank_name:
          !isWithholdingTax && paymentMode === 'Transfer'
            ? paymentBankName
            : '',
        cheque_number:
          !isWithholdingTax && paymentMode === 'Cheque'
            ? paymentChequeNumber
            : '',
        receipt_no: isWithholdingTax ? '' : paymentReceipt,
        amount: paymentAmount,
        remarks: paymentRemarks,
        bookid:
          paymentModal === 'specific_books' ? selectedPaymentBookIds[0] : '',
        bookids:
          paymentModal === 'specific_books' ? selectedPaymentBookIds : [],
        payment_type: paymentModal,
        entered_by: user?.userid ?? user?.user_id ?? user?.id,
      }
      await addOrderPayment(orderId, payload)
      setOrderPayments(await getOrderPayments(orderId))
      const orderResponse = await getOrderById(orderId)
      setOrder(orderResponse)
      const embeddedBooks = getValue(
        orderResponse,
        ['orderBooks', 'order_books', 'books', 'items'],
        null,
      )
      if (Array.isArray(embeddedBooks)) {
        setBooks(embeddedBooks)
      }
      closePaymentModal()
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSavingPayment(false)
    }
  }

  const confirmDeletePayment = async () => {
    if (!paymentToDelete) return
    const paymentId = getPaymentId(paymentToDelete)
    setDeletingPaymentId(paymentId)
    setError('')
    try {
      await deleteOrderPayment(paymentId)
      setOrderPayments(await getOrderPayments(orderId))
      const orderResponse = await getOrderById(orderId)
      setOrder(orderResponse)
      const embeddedBooks = getValue(
        orderResponse,
        ['orderBooks', 'order_books', 'books', 'items'],
        null,
      )
      if (Array.isArray(embeddedBooks)) {
        setBooks(embeddedBooks)
      }
      setPaymentToDelete(null)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setDeletingPaymentId(null)
    }
  }

  const lineItems = useMemo(
    () =>
      books.map((book) => {
        const quantity = getQuantity(book)
        const unitCost = getUnitCost(book)
        const discount = getDiscount(book)
        const gross = quantity * unitCost
        return {
          ...book,
          quantity,
          unitCost,
          discount,
          returned: getReturnedQuantity(book),
          total: gross - gross * (discount / 100),
        }
      }),
    [books],
  )
  const orderGrossTotal = lineItems.reduce(
    (sum, item) => sum + parseFloat(item.unitCost) * parseInt(item.quantity),
    0,
  )
  const orderDiscountTotal = lineItems.reduce(
    (sum, item) =>
      sum + parseFloat(item.discount_amt) * parseInt(item.quantity),
    0,
  )
  const orderNetTotal = useMemo(
    () =>
      lineItems.reduce((sum, item) => {
        const netUnitPrice = parseFloat(item.netunitprice)
        const lineTotal = !Number.isNaN(netUnitPrice)
          ? netUnitPrice * item.quantity
          : item.total
        return sum + (Number(lineTotal) || 0)
      }, 0),
    [lineItems],
  )

  const getBookBalance = useCallback(
    (item) => {
      if (!item) return 0
      const explicitBalance = getValue(
        item,
        [
          'balance',
          'balance_due',
          'amount_due',
          'due_amount',
          'item_balance',
          'book_balance',
          'remaining_amount',
          'rem_amount',
        ],
        null,
      )
      if (
        explicitBalance !== null &&
        explicitBalance !== undefined &&
        explicitBalance !== '-'
      ) {
        return Number(explicitBalance) || 0
      }

      const netUnitPrice = parseFloat(item.netunitprice)
      const subtotal = !Number.isNaN(netUnitPrice)
        ? netUnitPrice * item.quantity
        : item.total

      const paymentsForBook = (
        Array.isArray(orderPayments) ? orderPayments : []
      )
        .filter(
          (payment) =>
            String(getPaymentBookId(payment)) === String(getBookId(item)),
        )
        .reduce((sum, payment) => sum + getPaymentAmount(payment), 0)

      return subtotal - paymentsForBook
    },
    [orderPayments],
  )

  const availablePaymentBooks = useMemo(() => {
    return lineItems.filter(
      (item) =>
        getBookBalance(item) > 0 ||
        selectedPaymentBookIds.some(
          (bookId) => String(getBookId(item)) === String(bookId),
        ),
    )
  }, [lineItems, getBookBalance, selectedPaymentBookIds])

  const selectedPaymentBooks = useMemo(() => {
    if (
      paymentModal !== 'specific_books' ||
      selectedPaymentBookIds.length === 0
    )
      return []
    return lineItems.filter((item) =>
      selectedPaymentBookIds.some(
        (bookId) => String(getBookId(item)) === String(bookId),
      ),
    )
  }, [paymentModal, selectedPaymentBookIds, lineItems])
  const selectedPaymentBooksBalance = useMemo(
    () =>
      selectedPaymentBooks.reduce(
        (sum, item) => sum + Math.max(0, getBookBalance(item)),
        0,
      ),
    [selectedPaymentBooks, getBookBalance],
  )
  const totalAmtPaid = useMemo(() => {
    const explicitPaid = getValue(
      order,
      ['total_paid', 'totalPaid', 'paid_amount', 'amount_paid'],
      null,
    )
    if (
      explicitPaid !== null &&
      explicitPaid !== undefined &&
      explicitPaid !== '-'
    ) {
      return Number(explicitPaid) || 0
    }
    return (Array.isArray(orderPayments) ? orderPayments : []).reduce(
      (sum, payment) => sum + getPaymentAmount(payment),
      0,
    )
  }, [order, orderPayments])

  const orderBalance = useMemo(() => {
    const explicitBalance = getValue(
      order,
      ['balance', 'balance_due', 'amount_due', 'total_balance'],
      null,
    )
    if (
      explicitBalance !== null &&
      explicitBalance !== undefined &&
      explicitBalance !== '-'
    ) {
      return Number(explicitBalance) || 0
    }
    return Math.max(0, orderNetTotal - totalAmtPaid)
  }, [order, orderNetTotal, totalAmtPaid])

  const maxPaymentAmount = useMemo(() => {
    return orderBalance
  }, [orderBalance])

  const approved = getValue(order, ['approved_date', 'isApproved'], false) //isOrderApproved(order)
  const released = getValue(order, ['released_date', 'isReleased'], false) //isOrderReleased(order)

  const openInvoice = () => {
    const invoiceNumber = getOrderId(order)
    const invoiceData = {
      company: 'Buck Press Limited',
      orderType: getValue(order, ['order_type', 'orderType'], 'published_book'),
      order_type: getValue(
        order,
        ['order_type', 'orderType'],
        'published_book',
      ),
      customer: {
        name: getCustomerName(order),
        address: getValue(order, ['address', 'cust_address', 'location']),
        phone: getValue(order, ['phone', 'contact', 'cust_contact']),
      },
      invoiceNumber,
      salesExecutive: getValue(order, [
        'sales_exec_name',
        'salesExecutive',
        'sales_executive',
        'sales_exec',
      ]),
      requestingOfficer: getValue(order, [
        'requesting_user',
        'requested_by',
        'user_fullname',
        'fullname',
      ]),
      printDate: new Date().toISOString(),
      items: lineItems.map((item) => ({
        quantity: item.quantity,
        description: getBookTitle(item),
        unitCost: item.unitCost,
        discount: item.discount,
        subtotal: item.total,
        totalReturned: item.returned,
        aquantity: item.aquantity,
        witholdingTax: getValue(order, ['witholdingTax', 'withholding_tax'], 0),
      })),
      summary: {
        grossTotal: lineItems.reduce(
          (sum, item) =>
            sum + parseFloat(item.unitCost) * parseInt(item.quantity),
          0,
        ),
        discountTotal: lineItems.reduce(
          (sum, item) =>
            sum + parseFloat(item.discount_amt) * parseInt(item.quantity),
          0,
        ),
        NetTotal: lineItems.reduce(
          (sum, item) =>
            sum + parseFloat(item.netunitprice) * parseInt(item.quantity),
          0,
        ),
      },
    }
    openPrintableDocument(`/invoice/${encodeURIComponent(invoiceNumber)}`)
  }

  if (isLoading) {
    return (
      <div className='order-detail-page__state'>Loading order details...</div>
    )
  }
  if (error || !order) {
    return (
      <div className='order-detail-page__state order-detail-page__state--error'>
        <strong>Order unavailable</strong>
        <p>{error || 'This order could not be loaded.'}</p>
        <button type='button' onClick={() => navigate('/sales/orders')}>
          Back to orders
        </button>
      </div>
    )
  }

  return (
    <div className='order-detail-page'>
      <nav className='order-detail-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <Link to='/sales/orders'>Published orders</Link>
        <span aria-hidden='true'>/</span>
        <strong>Order {getOrderId(order)}</strong>
      </nav>

      <header className='order-detail-page__header'>
        <div>
          <span className='app-content__eyebrow'>Order workspace</span>
          <h1>Order {getOrderId(order)}</h1>
          <p>Review customer information, books ordered, and order activity.</p>
        </div>
        <span className='order-detail-status'>{getOrderStatus(order)}</span>
      </header>

      <div className='order-detail-layout'>
        <aside className='order-detail-sidebar'>
          <section className='order-detail-card order-detail-card--customer'>
            <div className='order-detail-card__heading'>
              <span className='order-detail-card__icon' aria-hidden='true'>
                ◇
              </span>
              <div>
                <span className='app-content__eyebrow'>Account</span>
                <h2>Customer info</h2>
              </div>
            </div>
            <dl className='order-detail-facts'>
              <div>
                <dt>Customer</dt>
                <dd>{getCustomerName(order)}</dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{getValue(order, ['phone', 'contact', 'cust_contact'])}</dd>
              </div>
              <div>
                <dt>Address</dt>
                <dd>
                  {getValue(order, ['address', 'cust_address', 'location'])}
                </dd>
              </div>
            </dl>
            {getOrderStatus(order).toLowerCase() === 'requested' && (
              <button
                className='order-detail-button order-detail-button--secondary'
                type='button'
                onClick={openCustomerModal}
              >
                Change Customer
              </button>
            )}
            {getOrderStatus(order).toLowerCase() !== 'requested' && (
              <button
                className='order-detail-button order-detail-button--secondary'
                type='button'
                onClick={() => navigate('/customers')}
              >
                View Customers
              </button>
            )}
          </section>

          <section className='order-detail-card'>
            <div className='order-detail-card__heading'>
              <span className='order-detail-card__icon' aria-hidden='true'>
                ◎
              </span>
              <div>
                <span className='app-content__eyebrow'>Timeline</span>
                <h2>Order activity</h2>
              </div>
            </div>
            <div className='order-timeline'>
              <div className='order-timeline__item order-timeline__item--active'>
                <span aria-hidden='true' />
                <div>
                  <strong>Order published</strong>
                  <small>
                    {formatDate(
                      getValue(
                        order,
                        ['request_date', 'order_date', 'created_at'],
                        '',
                      ),
                    )}
                  </small>
                </div>
              </div>
              <div className='order-timeline__item'>
                <span aria-hidden='true' />
                <div>
                  <strong>Sales executive</strong>
                  <small>
                    {getValue(order, [
                      'sales_exec_name',
                      'salesExecutive',
                      'sales_executive',
                      'sales_exec',
                    ])}
                  </small>
                </div>
              </div>
              <div className='order-timeline__item'>
                <span aria-hidden='true' />
                <div>
                  <strong>Requested by</strong>
                  <small>
                    {getValue(order, [
                      'requesting_user',
                      'requested_by',
                      'user_fullname',
                      'fullname',
                    ])}
                  </small>
                </div>
              </div>
              <div
                className={`order-timeline__item order-timeline__item--milestone ${
                  approved ? 'order-timeline__item--active' : ''
                }`}
              >
                <span aria-hidden='true'>{approved ? '✓' : '!'}</span>
                <div>
                  <strong>
                    {approved ? 'Order approved' : 'Approval pending'}
                  </strong>
                  <small>
                    {approved
                      ? `Officer: ${getValue(order, [
                          'approving_user',
                          'approver',
                          'approved_user',
                          'approval_officer',
                        ])}`
                      : 'Approve order'}
                  </small>
                </div>
                <div className='order-timeline__status'>
                  {approved && (
                    <small>
                      Approved on{' '}
                      {formatDate(
                        getValue(
                          order,
                          ['approved_date', 'approval_date', 'approve_date'],
                          '',
                        ),
                      )}
                    </small>
                  )}
                  {!approved && canApproveOrder && (
                    <button
                      className='order-timeline__action'
                      type='button'
                      onClick={() => setStatusModal('approve')}
                      disabled={isUpdatingStatus}
                    >
                      <span aria-hidden='true'>✓</span> Approve
                    </button>
                  )}
                  {getOrderStatus(order) === 'approved' &&
                    canUnapproveOrder && (
                      <button
                        className='order-timeline__action'
                        type='button'
                        onClick={() => setStatusModal('unapprove')}
                        disabled={isUpdatingStatus}
                      >
                        <span aria-hidden='true'>↶</span>{' '}
                        {isUpdatingStatus ? 'Updating...' : 'Unapprove'}
                      </button>
                    )}
                  {getOrderStatus(order) !== 'requested' && (
                    <button
                      className='order-timeline__action'
                      type='button'
                      onClick={() =>
                        openPrintableDocument(
                          `/order/deliverynote/${getOrderId(order)}`,
                        )
                      }
                    >
                      View Delivery Note
                    </button>
                  )}
                </div>
              </div>
              <div
                className={`order-timeline__item order-timeline__item--milestone ${
                  released ? 'order-timeline__item--active' : ''
                }`}
              >
                <span aria-hidden='true'>{released ? '✓' : '!'}</span>
                <div>
                  <strong>
                    {released ? 'Order released' : 'Release pending'}
                  </strong>
                  <small>
                    {released
                      ? `Officer: ${getValue(order, [
                          'releasing_user',
                          'release_user',
                          'release_officer',
                          'released_at',
                        ])}`
                      : 'Order not delivered! Go to stores to release for delivery'}
                  </small>
                </div>
                <div className='order-timeline__status'>
                  {released ? (
                    <small>
                      Released on{' '}
                      {formatDate(
                        getValue(
                          order,
                          ['released_date', 'release_date', 'released_date'],
                          '',
                        ),
                      )}
                    </small>
                  ) : canReleaseOrder ? (
                    <button
                      className='order-timeline__action'
                      type='button'
                      onClick={() => navigate('/admin/stores')}
                    >
                      <span aria-hidden='true'>→</span> Release
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
            <button
              className='order-detail-button order-detail-button--primary'
              type='button'
              onClick={openInvoice}
            >
              Invoice
            </button>
          </section>
        </aside>

        <main className='order-detail-main'>
          <section className='order-detail-card order-detail-card--books'>
            <div className='order-detail-section-heading'>
              <div>
                <span className='app-content__eyebrow'>Fulfilment</span>
                <h2>Books ordered</h2>
              </div>
              <span className='order-detail-count'>
                {lineItems.length} titles
              </span>
            </div>
            <div className='order-detail-note'>
              <strong aria-hidden='true'>i</strong>
              <span>
                Quantities in parentheses show returned units. The quantity
                outside parentheses is the current order quantity.
              </span>
            </div>
            <div className='order-detail-table-wrap'>
              <table className='order-detail-table'>
                <thead>
                  <tr>
                    <th>Qty ordered</th>
                    <th>Description</th>
                    <th>Unit cost</th>
                    <th>Discount</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length ? (
                    lineItems.map((item, index) => (
                      <tr key={`${getBookTitle(item)}-${index}`}>
                        <td>
                          {item.quantity}
                          {item.returned > 0 && (
                            <small> ({item.returned})</small>
                          )}
                        </td>
                        <td>
                          <strong>{getBookTitle(item)}</strong>
                        </td>
                        <td>{formatCurrency(item.unitCost)}</td>
                        <td>{item.discount.toFixed(2)}%</td>
                        <td>
                          <strong>{formatCurrency(item.total)}</strong>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan='5' className='order-detail-table__empty'>
                        No books were recorded for this order.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className='order-detail-totals'>
              <div>
                <span>Gross total</span>
                <strong>{formatCurrency(orderGrossTotal)}</strong>
              </div>
              <div>
                <span>Discount</span>
                <strong>{formatCurrency(orderDiscountTotal)}</strong>
              </div>
              <div className='order-detail-totals__grand'>
                <span>Net total</span>
                <strong>{formatCurrency(orderNetTotal)}</strong>
              </div>
            </div>
          </section>

          <section className='order-detail-card order-detail-card--activity'>
            <div
              className='order-detail-tabs'
              role='tablist'
              aria-label='Order activity'
            >
              {[
                ['payment-plan', 'Payment plan'],
                ['payments', 'Payments'],
                ['returns', 'Returns'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  className={activeTab === value ? 'is-active' : ''}
                  type='button'
                  role='tab'
                  aria-selected={activeTab === value}
                  onClick={() => {
                    setActiveTab(value)
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className='order-detail-tab-panel' role='tabpanel'>
              {activeTab === 'payment-plan' &&
                (getOrderStatus(order).toLowerCase() === 'requested' ? (
                  <div className='order-detail-empty-panel'>
                    <span
                      className='order-detail-empty-panel__mark'
                      aria-hidden='true'
                    >
                      +
                    </span>
                    <strong>No payment plan for this order</strong>
                    <p>
                      Payment schedules and due dates will appear here when they
                      are added.
                    </p>
                    <button
                      type='button'
                      onClick={() => {
                        setActiveTab('payments')
                      }}
                    >
                      View payment history
                    </button>
                  </div>
                ) : (
                  <div className='order-detail-payment-plan'>
                    {approved &&
                      canAddPaymentPlan &&
                      getOrderStatus(order).toLowerCase() !== 'paid' && (
                        <div className='order-detail-payment-plan__header'>
                          <button
                            className='order-detail-button order-detail-button--secondary'
                            type='button'
                            onClick={openPaymentPlanModal}
                          >
                            + Add payment plan
                          </button>
                        </div>
                      )}
                    <div className='order-detail-table-wrap'>
                      <table className='order-detail-table'>
                        <thead>
                          <tr>
                            <th>S/N</th>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(Array.isArray(orderPaymentPlan)
                            ? orderPaymentPlan
                            : []
                          ).length ? (
                            (Array.isArray(orderPaymentPlan)
                              ? orderPaymentPlan
                              : []
                            ).map((item, index) => (
                              <tr key={`payment-plan-${index}`}>
                                <td>{index + 1}</td>
                                <td>{formatDate(getPaymentPlanDate(item))}</td>
                                <td>
                                  {formatCurrency(getPaymentPlanAmount(item))}
                                </td>
                                <td>{getPaymentPlanStatus(item)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan='4'
                                className='order-detail-table__empty'
                              >
                                No payment plan items recorded.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              {activeTab === 'payments' &&
                (getOrderStatus(order).toLowerCase() === 'requested' ? (
                  <div className='order-detail-empty-panel'>
                    <span
                      className='order-detail-empty-panel__mark'
                      aria-hidden='true'
                    >
                      ₵
                    </span>
                    <strong>No payments recorded</strong>
                    <p>
                      Payments made against this order will be listed in this
                      space once order has been delivered.
                    </p>
                  </div>
                ) : (
                  <div className='order-detail-payment-plan'>
                    <div className='order-detail-payment-plan__header'>
                      <div className='order-detail-payment-summary'>
                        <span>
                          Total Amt Paid:{' '}
                          <strong>{formatCurrency(totalAmtPaid)}</strong>
                        </span>
                        <span>
                          Balance:{' '}
                          <strong>{formatCurrency(orderBalance)}</strong>
                        </span>
                      </div>
                      {canAddPayment &&
                        getOrderStatus(order).toLowerCase() !== 'paid' && (
                          <div className='order-detail-pay-split'>
                            <button
                              className='order-detail-pay-split__main'
                              type='button'
                              onClick={() => openPaymentModal('general')}
                            >
                              + Pay
                            </button>
                            <button
                              className='order-detail-pay-split__toggle'
                              type='button'
                              aria-label='Choose payment type'
                              aria-expanded={isPaymentTypeMenuOpen}
                              onClick={() =>
                                setIsPaymentTypeMenuOpen((open) => !open)
                              }
                            >
                              ▾
                            </button>
                            {isPaymentTypeMenuOpen && (
                              <div className='order-detail-pay-split__menu'>
                                <button
                                  type='button'
                                  onClick={() =>
                                    openPaymentModal('specific_books')
                                  }
                                >
                                  Specific Books
                                </button>
                                <button
                                  type='button'
                                  onClick={() =>
                                    openPaymentModal('withholding_tax')
                                  }
                                >
                                  With holding tax
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                    {getOrderStatus(order).toLowerCase() === 'paid' && (
                      <div className='order-detail-payment-notice'>
                        <p>
                          No payment can be made to this order due to one of the
                          reasons below
                        </p>
                        <ol>
                          <li>Order may have not be delivered yet.</li>
                          <li>Full payment made for order.</li>
                        </ol>
                      </div>
                    )}
                    <div className='order-detail-table-wrap'>
                      <table className='order-detail-table'>
                        <thead>
                          <tr>
                            <th>S/N</th>
                            <th>Date</th>
                            <th>Pay mode</th>
                            <th>Receipt</th>
                            <th>Amount</th>
                            <th>Entered by</th>
                            <th>Remarks</th>
                            <th>
                              <span className='sr-only'>Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(Array.isArray(orderPayments) ? orderPayments : [])
                            .length ? (
                            (Array.isArray(orderPayments)
                              ? orderPayments
                              : []
                            ).map((payment, index) => (
                              <tr key={getPaymentId(payment) ?? index}>
                                <td>{index + 1}</td>
                                <td>{formatDate(getPaymentDate(payment))}</td>
                                <td>{getPaymentMode(payment)}</td>
                                <td>{getPaymentReceipt(payment)}</td>
                                <td>
                                  {formatCurrency(getPaymentAmount(payment))}
                                </td>
                                <td>{getPaymentEnteredBy(payment)}</td>
                                <td>{getPaymentRemarks(payment)}</td>
                                <td>
                                  <div className='order-detail-table__actions'>
                                    {(getPaymentMode(payment) === 'Cheque' ||
                                      getPaymentMode(payment) ===
                                        'Transfer') && (
                                      <button
                                        type='button'
                                        onClick={() =>
                                          setViewingPayment(payment)
                                        }
                                      >
                                        View
                                      </button>
                                    )}
                                    {canDeletePayment && (
                                      <button
                                        type='button'
                                        onClick={() =>
                                          setPaymentToDelete(payment)
                                        }
                                        disabled={
                                          deletingPaymentId ===
                                          getPaymentId(payment)
                                        }
                                      >
                                        {deletingPaymentId ===
                                        getPaymentId(payment)
                                          ? 'Deleting...'
                                          : 'Delete'}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan='8'
                                className='order-detail-table__empty'
                              >
                                No payments recorded.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              {activeTab === 'returns' &&
                (() => {
                  const returnedItems = lineItems.filter(
                    (item) => item.returned > 0,
                  )
                  return returnedItems.length ? (
                    <div className='order-detail-payment-plan'>
                      <dl className='order-detail-returns-meta'>
                        <div>
                          <dt>Items returned date</dt>
                          <dd>{formatDate(orderReturns.returned_date)}</dd>
                        </div>
                        <div>
                          <dt>Items returned by</dt>
                          <dd>{orderReturns.returning_user}</dd>
                        </div>
                        <div>
                          <dt>Comment</dt>
                          <dd>{orderReturns.return_comments}</dd>
                        </div>
                      </dl>
                      <div className='order-detail-table-wrap'>
                        <table className='order-detail-table'>
                          <thead>
                            <tr>
                              <th>Books</th>
                              <th>No. Returned</th>
                              <th>Good</th>
                              <th>Bad</th>
                              <th>Missing</th>
                            </tr>
                          </thead>
                          <tbody>
                            {returnedItems.map((item, index) => (
                              <tr key={`book-${index}`}>
                                <td>
                                  <strong>{getBookTitle(item)}</strong>
                                </td>
                                <td>{item.returned}</td>
                                <td>{item.returned_good || 0}</td>
                                <td>{item.returned_bad || 0}</td>
                                <td>{item.returned_missing || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className='order-detail-empty-panel'>
                      <span
                        className='order-detail-empty-panel__mark'
                        aria-hidden='true'
                      >
                        ↩
                      </span>
                      <strong>No returns recorded</strong>
                      <p>
                        Returned books and quantity adjustments will appear
                        here.
                      </p>
                    </div>
                  )
                })()}
            </div>
          </section>
        </main>
      </div>
      {statusModal && (
        <div
          className='order-approval-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isUpdatingStatus &&
            setStatusModal(null)
          }
        >
          <section
            className='order-approval-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='order-status-modal-title'
          >
            <div className='order-approval-modal__header'>
              <div>
                <span className='app-content__eyebrow'>
                  {statusModal === 'approve'
                    ? 'Order approval'
                    : 'Order unapproval'}
                </span>
                <h2 id='order-status-modal-title'>
                  {statusModal === 'approve'
                    ? 'Approve order?'
                    : 'Unapprove order?'}
                </h2>
              </div>
              <button
                type='button'
                onClick={() => setStatusModal(null)}
                aria-label='Close status modal'
                disabled={isUpdatingStatus}
              >
                x
              </button>
            </div>
            <form
              className='order-approval-modal__form'
              onSubmit={submitStatusChange}
            >
              {statusModal === 'approve' ? (
                <>
                  <label>
                    Approval date
                    <input
                      type='date'
                      value={statusDate}
                      onChange={(event) => setStatusDate(event.target.value)}
                      required
                      disabled={isUpdatingStatus}
                    />
                  </label>
                  <label>
                    Approval comment
                    <textarea
                      value={statusComment}
                      onChange={(event) => setStatusComment(event.target.value)}
                      placeholder='Add a comment about this approval'
                      rows='4'
                      required
                      disabled={isUpdatingStatus}
                    />
                  </label>
                </>
              ) : (
                <p>
                  This order will be unapproved and its approval date and
                  comment will be cleared.
                </p>
              )}
              <div className='order-approval-modal__actions'>
                <button
                  type='button'
                  onClick={() => setStatusModal(null)}
                  disabled={isUpdatingStatus}
                >
                  Cancel
                </button>
                <button type='submit' disabled={isUpdatingStatus}>
                  {isUpdatingStatus
                    ? 'Updating...'
                    : statusModal === 'approve'
                      ? 'Approve order'
                      : 'Unapprove order'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      {isCustomerModalOpen && (
        <div
          className='order-approval-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isUpdatingCustomer &&
            setIsCustomerModalOpen(false)
          }
        >
          <section
            className='order-approval-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='customer-change-modal-title'
          >
            <div className='order-approval-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Customer</span>
                <h2 id='customer-change-modal-title'>Change customer</h2>
              </div>
              <button
                type='button'
                onClick={() => setIsCustomerModalOpen(false)}
                aria-label='Close customer modal'
                disabled={isUpdatingCustomer}
              >
                x
              </button>
            </div>
            <form
              className='order-approval-modal__form'
              onSubmit={submitCustomerChange}
            >
              <label>
                Customer type
                <select
                  value={customerTypeOption}
                  onChange={handleCustomerTypeOptionChange}
                  disabled={isUpdatingCustomer}
                >
                  <option value=''>All customer types</option>
                  {customerTypes.map((type) => (
                    <option
                      key={getOptionValue(type)}
                      value={getOptionValue(type)}
                    >
                      {getOptionLabel(type)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Customer
                <select
                  value={selectedCustomerId}
                  onChange={(event) =>
                    setSelectedCustomerId(event.target.value)
                  }
                  required
                  disabled={isUpdatingCustomer}
                >
                  <option value=''>Select a customer</option>
                  {customers.map((customer) => (
                    <option
                      key={getValue(customer, ['customerid', 'id'])}
                      value={getValue(customer, ['customerid', 'id'])}
                    >
                      {getValue(customer, ['cust_name', 'name', 'company'])}
                    </option>
                  ))}
                </select>
              </label>
              <div className='order-approval-modal__actions'>
                <button
                  type='button'
                  onClick={() => setIsCustomerModalOpen(false)}
                  disabled={isUpdatingCustomer}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={isUpdatingCustomer || !selectedCustomerId}
                >
                  {isUpdatingCustomer ? 'Updating...' : 'Change customer'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      {isPaymentPlanModalOpen && (
        <div
          className='order-approval-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isAddingPaymentPlan &&
            setIsPaymentPlanModalOpen(false)
          }
        >
          <section
            className='order-approval-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='payment-plan-modal-title'
          >
            <div className='order-approval-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Payment plan</span>
                <h2 id='payment-plan-modal-title'>Add payment plan</h2>
              </div>
              <button
                type='button'
                onClick={() => setIsPaymentPlanModalOpen(false)}
                aria-label='Close payment plan modal'
                disabled={isAddingPaymentPlan}
              >
                x
              </button>
            </div>
            <form
              className='order-approval-modal__form'
              onSubmit={submitPaymentPlan}
            >
              <label>
                Date
                <input
                  type='date'
                  value={paymentPlanDate}
                  onChange={(event) => setPaymentPlanDate(event.target.value)}
                  required
                  disabled={isAddingPaymentPlan}
                />
              </label>
              <label>
                Amount
                <input
                  type='number'
                  min='0'
                  step='0.01'
                  value={paymentPlanAmount}
                  onChange={(event) => setPaymentPlanAmount(event.target.value)}
                  placeholder='Enter amount'
                  required
                  disabled={isAddingPaymentPlan}
                />
              </label>
              <div className='order-approval-modal__actions'>
                <button
                  type='button'
                  onClick={() => setIsPaymentPlanModalOpen(false)}
                  disabled={isAddingPaymentPlan}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={isAddingPaymentPlan || !paymentPlanAmount}
                >
                  {isAddingPaymentPlan ? 'Adding...' : 'Add payment plan'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      {paymentModal && (
        <div
          className='order-approval-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isSavingPayment &&
            closePaymentModal()
          }
        >
          <section
            className='order-approval-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='payment-modal-title'
          >
            <div className='order-approval-modal__header'>
              <div>
                <span className='app-content__eyebrow'>
                  {paymentModal === 'withholding_tax'
                    ? 'With holding tax'
                    : paymentModal === 'specific_books'
                      ? 'Specific Books'
                      : 'Payment'}
                </span>
                <h2 id='payment-modal-title'>Add payment</h2>
              </div>
              <button
                type='button'
                onClick={closePaymentModal}
                aria-label='Close payment modal'
                disabled={isSavingPayment}
              >
                x
              </button>
            </div>
            <form
              className='order-approval-modal__form'
              onSubmit={submitPayment}
            >
              <label>
                Date
                <input
                  type='date'
                  value={paymentDate}
                  onChange={(event) => setPaymentDate(event.target.value)}
                  required
                  disabled={isSavingPayment}
                />
              </label>
              {paymentModal === 'withholding_tax' ? (
                <>
                  <label>
                    Amount
                    <input
                      type='number'
                      min='0'
                      max={maxPaymentAmount}
                      step='0.01'
                      value={paymentAmount}
                      onChange={(event) => setPaymentAmount(event.target.value)}
                      placeholder='Enter amount'
                      required
                      disabled={isSavingPayment}
                    />
                  </label>
                  <label>
                    Comments
                    <textarea
                      value={paymentRemarks}
                      onChange={(event) =>
                        setPaymentRemarks(event.target.value)
                      }
                      placeholder='Add a comment about this payment'
                      rows='3'
                      required
                      disabled={isSavingPayment}
                    />
                  </label>
                </>
              ) : (
                <>
                  <label>
                    Pay mode
                    <select
                      value={paymentMode}
                      onChange={(event) => setPaymentMode(event.target.value)}
                      disabled={isSavingPayment}
                      required
                    >
                      <option value='Cash'>Cash</option>
                      <option value='Mobile Money'>Mobile Money</option>
                      <option value='Cheque'>Cheque</option>
                      <option value='Transfer'>Bank Transfer</option>
                    </select>
                  </label>
                  {paymentModal === 'specific_books' && (
                    <label>
                      Books
                      <select
                        multiple
                        value={selectedPaymentBookIds}
                        onChange={(event) =>
                          setSelectedPaymentBookIds(
                            Array.from(
                              event.target.selectedOptions,
                              (option) => option.value,
                            ),
                          )
                        }
                        required
                        disabled={isSavingPayment}
                      >
                        {availablePaymentBooks.map((book) => (
                          <option key={getBookId(book)} value={getBookId(book)}>
                            {getBookTitle(book)} -{' '}
                            {formatCurrency(getBookBalance(book))}
                          </option>
                        ))}
                      </select>
                      {selectedPaymentBookIds.length > 0 && (
                        <small>
                          Selected balance:{' '}
                          {formatCurrency(selectedPaymentBooksBalance)}
                        </small>
                      )}
                    </label>
                  )}
                  {paymentMode === 'Cheque' && (
                    <>
                      <label>
                        Cheque number
                        <input
                          type='text'
                          value={paymentChequeNumber}
                          onChange={(event) =>
                            setPaymentChequeNumber(event.target.value)
                          }
                          placeholder='Enter cheque number'
                          required
                          disabled={isSavingPayment}
                        />
                      </label>
                    </>
                  )}
                  {paymentMode === 'Transfer' && (
                    <label>
                      Bank name
                      <input
                        type='text'
                        value={paymentBankName}
                        onChange={(event) =>
                          setPaymentBankName(event.target.value)
                        }
                        placeholder='Enter bank name'
                        required
                        disabled={isSavingPayment}
                      />
                    </label>
                  )}
                  <label>
                    Receipt
                    <input
                      type='text'
                      value={paymentReceipt}
                      onChange={(event) =>
                        setPaymentReceipt(event.target.value)
                      }
                      placeholder='Enter receipt number'
                      required
                      disabled={isSavingPayment}
                    />
                  </label>
                  <label>
                    Amount
                    <input
                      type='number'
                      min='0'
                      max={maxPaymentAmount}
                      step='0.01'
                      value={paymentAmount}
                      onChange={(event) => setPaymentAmount(event.target.value)}
                      placeholder='Enter amount'
                      required
                      disabled={isSavingPayment}
                    />
                  </label>
                  <label>
                    Remarks
                    <textarea
                      value={paymentRemarks}
                      onChange={(event) =>
                        setPaymentRemarks(event.target.value)
                      }
                      placeholder='Add a remark about this payment'
                      rows='3'
                      required
                      disabled={isSavingPayment}
                    />
                  </label>
                </>
              )}
              <div className='order-approval-modal__actions'>
                <button
                  type='button'
                  onClick={closePaymentModal}
                  disabled={isSavingPayment}
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={isSavingPayment || !paymentAmount}
                >
                  {isSavingPayment ? 'Saving...' : 'Add payment'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      {paymentToDelete && (
        <div
          className='order-approval-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !deletingPaymentId &&
            setPaymentToDelete(null)
          }
        >
          <section
            className='order-approval-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='delete-payment-modal-title'
          >
            <div className='order-approval-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Delete payment</span>
                <h2 id='delete-payment-modal-title'>Delete payment record?</h2>
              </div>
              <button
                type='button'
                onClick={() => setPaymentToDelete(null)}
                aria-label='Close delete payment modal'
                disabled={Boolean(deletingPaymentId)}
              >
                x
              </button>
            </div>
            <div className='order-approval-modal__form'>
              <p>
                Are you sure you want to delete this payment of{' '}
                <strong>
                  {formatCurrency(getPaymentAmount(paymentToDelete))}
                </strong>
                ? This action cannot be undone.
              </p>
              <div className='order-approval-modal__actions'>
                <button
                  type='button'
                  onClick={() => setPaymentToDelete(null)}
                  disabled={Boolean(deletingPaymentId)}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={confirmDeletePayment}
                  disabled={Boolean(deletingPaymentId)}
                >
                  {deletingPaymentId ? 'Deleting...' : 'Delete payment'}
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
      {viewingPayment && (
        <div
          className='order-approval-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && setViewingPayment(null)
          }
        >
          <section
            className='order-approval-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='view-payment-modal-title'
          >
            <div className='order-approval-modal__header'>
              <div>
                <span className='app-content__eyebrow'>
                  {getPaymentMode(viewingPayment)} details
                </span>
                <h2 id='view-payment-modal-title'>Payment details</h2>
              </div>
              <button
                type='button'
                onClick={() => setViewingPayment(null)}
                aria-label='Close payment details modal'
              >
                x
              </button>
            </div>
            <div className='order-approval-modal__form'>
              <dl className='order-detail-facts' style={{ margin: 0 }}>
                <div>
                  <dt>Payment Date</dt>
                  <dd>{formatDate(getPaymentDate(viewingPayment))}</dd>
                </div>
                <div>
                  <dt>Pay Mode</dt>
                  <dd>{getPaymentMode(viewingPayment)}</dd>
                </div>
                {getPaymentMode(viewingPayment) === 'Cheque' && (
                  <div>
                    <dt>Cheque Number</dt>
                    <dd>{getPaymentChequeNumber(viewingPayment) || '-'}</dd>
                  </div>
                )}
                {getPaymentMode(viewingPayment) === 'Transfer' && (
                  <div>
                    <dt>Bank Name</dt>
                    <dd>{getPaymentBankName(viewingPayment) || '-'}</dd>
                  </div>
                )}
                <div>
                  <dt>Receipt Number</dt>
                  <dd>{getPaymentReceipt(viewingPayment) || '-'}</dd>
                </div>
                <div>
                  <dt>Amount</dt>
                  <dd>
                    <strong>
                      {formatCurrency(getPaymentAmount(viewingPayment))}
                    </strong>
                  </dd>
                </div>
                <div>
                  <dt>Entered By</dt>
                  <dd>{getPaymentEnteredBy(viewingPayment) || '-'}</dd>
                </div>
                <div>
                  <dt>Remarks</dt>
                  <dd>{getPaymentRemarks(viewingPayment) || '-'}</dd>
                </div>
              </dl>
              <div className='order-approval-modal__actions'>
                <button type='button' onClick={() => setViewingPayment(null)}>
                  Close
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default OderDetail
